/**
 * Property Management Constants
 * Defines property types, amenities, statuses, and other reference data
 */

export const PROPERTY_TYPES = [
    { id: 1, name: 'Apartment', description: 'Multi-unit residential building' },
    { id: 2, name: 'House', description: 'Single-family detached house' },
    { id: 3, name: 'Townhouse', description: 'Multi-floor home sharing walls' },
    { id: 4, name: 'Condo', description: 'Individually owned unit in a complex' },
    { id: 5, name: 'Studio', description: 'Single-room living space' },
    { id: 6, name: 'Villa', description: 'Luxury detached house' },
    { id: 7, name: 'Bedsitter', description: 'Single room with bathroom and kitchen' }
];

export const AMENITIES = [
    { id: 1, name: 'WiFi', category: 'utilities', icon: 'Wifi' },
    { id: 2, name: 'Parking', category: 'facilities', icon: 'Car' },
    { id: 3, name: 'Swimming Pool', category: 'recreation', icon: 'Waves' },
    { id: 4, name: 'Gym', category: 'recreation', icon: 'Dumbbell' },
    { id: 5, name: '24/7 Security', category: 'safety', icon: 'Shield' },
    { id: 6, name: 'Elevator', category: 'facilities', icon: 'ArrowUpDown' },
    { id: 7, name: 'Balcony', category: 'features', icon: 'Home' },
    { id: 8, name: 'Garden', category: 'outdoor', icon: 'Trees' },
    { id: 9, name: 'Laundry', category: 'utilities', icon: 'Shirt' },
    { id: 10, name: 'Air Conditioning', category: 'utilities', icon: 'Wind' },
    { id: 11, name: 'Backup Generator', category: 'utilities', icon: 'Zap' },
    { id: 12, name: 'Water Backup', category: 'utilities', icon: 'Droplet' }
];

export const PROPERTY_STATUSES = {
    DRAFT: {
        value: 'draft',
        label: 'Draft',
        color: 'gray',
        description: 'Being created/edited',
        canEdit: true,
        canDelete: true
    },
    PENDING_APPROVAL: {
        value: 'pending_approval',
        label: 'Pending Approval',
        color: 'yellow',
        description: 'Awaiting admin review',
        canEdit: false,
        canDelete: true
    },
    ACTIVE: {
        value: 'active',
        label: 'Published',
        color: 'green',
        description: 'Published and live',
        canEdit: true,
        canDelete: true
    },
    ARCHIVED: {
        value: 'archived',
        label: 'Archived',
        color: 'blue',
        description: 'Hidden from public',
        canEdit: true,
        canDelete: true
    },
    INACTIVE: {
        value: 'inactive',
        label: 'Deleted',
        color: 'red',
        description: 'Soft deleted',
        canEdit: false,
        canDelete: false
    }
};

export const FURNISHING_STATUS = [
    { value: 'unfurnished', label: 'Unfurnished', description: 'Empty property' },
    { value: 'semi-furnished', label: 'Semi-Furnished', description: 'Basic furniture included' },
    { value: 'furnished', label: 'Furnished', description: 'Fully furnished with appliances' },
    { value: 'luxury-furnished', label: 'Luxury Furnished', description: 'High-end furniture and appliances' }
];

export const LEASE_TYPES = [
    { value: 'monthly', label: 'Monthly', description: 'Month-to-month lease' },
    { value: 'quarterly', label: 'Quarterly', description: '3-month lease' },
    { value: 'semi-annual', label: 'Semi-Annual', description: '6-month lease' },
    { value: 'annual', label: 'Annual', description: '12-month lease' },
    { value: 'flexible', label: 'Flexible', description: 'Negotiable lease terms' }
];

export const CURRENCIES = [
    { value: 'KES', label: 'KES (Kenyan Shilling)', symbol: 'KSh' },
    { value: 'USD', label: 'USD (US Dollar)', symbol: '$' },
    { value: 'EUR', label: 'EUR (Euro)', symbol: '€' },
    { value: 'GBP', label: 'GBP (British Pound)', symbol: '£' }
];

export const IMAGE_UPLOAD_CONFIG = {
    MAX_SIZE: 10 * 1024 * 1024, // 10MB in bytes
    ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
    ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    MAX_FILES: 20
};

export const VALIDATION_RULES = {
    PROPERTY_NAME: {
        required: true,
        minLength: 5,
        maxLength: 255
    },
    ADDRESS: {
        required: true,
        minLength: 5,
        maxLength: 500
    },
    CITY: {
        required: true,
        minLength: 2,
        maxLength: 100
    },
    DESCRIPTION: {
        minLength: 20,
        maxLength: 5000
    },
    PRICE: {
        required: true,
        min: 0
    },
    BEDROOMS: {
        min: 0,
        max: 20
    },
    BATHROOMS: {
        min: 0,
        max: 20
    },
    SQUARE_FEET: {
        min: 0,
        max: 100000
    }
};

export const FORM_STEPS = [
    { id: 1, name: 'Basic Information', icon: 'Info' },
    { id: 2, name: 'Property Details', icon: 'Home' },
    { id: 3, name: 'Pricing & Terms', icon: 'DollarSign' },
    { id: 4, name: 'Features & Amenities', icon: 'Star' },
    { id: 5, name: 'Images', icon: 'Image' },
    { id: 6, name: 'Additional Info', icon: 'FileText' },
    { id: 7, name: 'Review', icon: 'CheckCircle' }
];

/**
 * Get status configuration by value
 */
export const getStatusConfig = (status) => {
    return Object.values(PROPERTY_STATUSES).find(s => s.value === status) || PROPERTY_STATUSES.DRAFT;
};

/**
 * Get status badge color for Tailwind CSS
 */
export const getStatusBadgeClasses = (status) => {
    const config = getStatusConfig(status);
    const colorMap = {
        gray: 'bg-soil-100 text-soil-600',
        yellow: 'bg-dawn-100 text-dawn-600',
        green: 'bg-highland-50 text-highland-600',
        blue: 'bg-jacaranda-100 text-jacaranda-600',
        red: 'bg-murram-100 text-murram-600'
    };

    return colorMap[config.color] || colorMap.gray;
};

/**
 * Format currency with symbol
 */
export const formatCurrency = (amount, currencyCode = 'KES') => {
    const currency = CURRENCIES.find(c => c.value === currencyCode);
    const symbol = currency ? currency.symbol : currencyCode;

    return `${symbol} ${Number(amount).toLocaleString()}`;
};

/**
 * Get property type name by ID
 */
export const getPropertyTypeName = (typeId) => {
    const type = PROPERTY_TYPES.find(t => t.id === typeId);
    return type ? type.name : 'Unknown';
};

/**
 * Get amenities by IDs
 */
export const getAmenitiesByIds = (amenityIds) => {
    return AMENITIES.filter(a => amenityIds.includes(a.id));
};

/**
 * Validate image file
 */
export const validateImageFile = (file) => {
    const errors = [];

    if (file.size > IMAGE_UPLOAD_CONFIG.MAX_SIZE) {
        errors.push(`File size must be less than ${IMAGE_UPLOAD_CONFIG.MAX_SIZE / (1024 * 1024)}MB`);
    }

    if (!IMAGE_UPLOAD_CONFIG.ALLOWED_TYPES.includes(file.type)) {
        errors.push(`File type must be one of: ${IMAGE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(', ')}`);
    }

    return errors;
};
